<template>
  <div>
    <!-- Page Title -->
    <h2 class="page-title-bar">
      <img class="imgageproperty" src="@/assets/img/errorlist.png" alt="icon" />
      {{ $t("COO Assignments") }}
    </h2>

    <!-- Search & Manage Columns Row -->
    <v-row class="pa-0 ma-0 mt-4 searchArea">
      <!-- Manage Columns -->
      <v-col cols="12" md="2" class="d-flex align-center pb-0">
        <v-menu v-model="menuOpen" offset-y :close-on-content-click="false" content-class="columnMenu">
          <template v-slot:activator="{ on, attrs }">
            <v-btn text small class="manage-btn btn" v-bind="attrs" v-on="on">
              {{ $t("Manage Columns") }}
              <v-icon right :class="{ rotate: menuOpen }">mdi-chevron-down</v-icon>
            </v-btn>
          </template>
          <v-card class="column-card">
            <v-card-title class="column-header">{{ $t("Select Columns") }}</v-card-title>
            <v-divider />
            <v-list dense class="select-all">
              <v-list-item>
                <v-checkbox :input-value="isAllSelected" @change="toggleSelectAll" hide-details />
                <v-list-item-title>{{ $t("Select All") }}</v-list-item-title>
              </v-list-item>
            </v-list>
            <v-divider />
            <v-card-text class="column-list">
              <v-list dense>
                <v-list-item v-for="header in tableHeaders.filter(h => h.value !== 'labelCode')" :key="header.value">
                  <v-checkbox v-model="visibleColumns" :value="header.text" hide-details />
                  <v-list-item-title class="column-text">{{ header.text }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-card-text>
            <v-divider />
            <v-card-actions class="column-footer">
              <v-btn color="error" style="background:#c57171;color:#fff!important;width:80px" text small @click="menuOpen = false">
                {{ $t("Cancel") }}
              </v-btn>
              <v-spacer />
              <v-btn color="primary" small style="width:80px" @click="menuOpen = false;SaveVisibleColumns()">
                {{ $t("Save") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>
      </v-col>
      <!-- Vertical Divider -->
      <v-col cols="1" class="d-none d-md-flex align-center justify-center pa-0" style="align-self: end;">
        <v-divider vertical style="min-height: 40px;"></v-divider>
      </v-col>
      <!-- Search Fields -->
      <v-col cols="12" md="9" class="d-flex align-end py-0 coo-search-row">
        <v-text-field
          v-model="searchBy.labelId"
          :label="$t('Label ID')"
          :placeholder="$t('Input Label ID')"
          class="form-input coo-field"
          outlined dense hide-details clearable
          @keyup.enter="search(1)"
        ></v-text-field>
        <v-text-field
          v-model="searchBy.productId"
          :label="$t('Product ID')"
          :placeholder="$t('Input Product ID')"
          class="form-input coo-field"
          outlined dense hide-details clearable
          @keyup.enter="search(1)"
        ></v-text-field>
        <v-text-field
          v-model="searchBy.coo"
          :label="$t('COO')"
          :placeholder="$t('Input COO')"
          class="form-input coo-field"
          outlined dense hide-details clearable
          @keyup.enter="search(1)"
        ></v-text-field>
        <v-btn @click="search(1)" class="btn type-search coo-btn" text>{{ $t('Search') }}</v-btn>
        <v-btn @click="clear()" class="btn type-search coo-btn" text>{{ $t('Clear') }}</v-btn>
      </v-col>
    </v-row>

    <!-- Data Table -->
    <v-data-table
      v-model="selected"
      :headers="filteredHeaders"
      :items="tableItems"
      item-key="labelCode"
      :hide-default-footer="true"
      :options.sync="options"
      :server-items-length="totalRecords"
      class="tbl-type01 mt-10"
      show-select
    >
      <template slot="no-data">
        <p>{{ $t("No data available") }}</p>
      </template>

      <!-- Battery -->
      <template v-slot:[`item.battery`]="{ item }">
        <span :class="item.battery === 'GOOD' ? 'txt-stable' : 'txt-danger'">{{ item.battery }}</span>
      </template>

      <!-- Network dot -->
      <template v-slot:item.networkStatus="{ item }">
        <v-tooltip top content-class="tooltip-content">
          <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on" class="d-flex justify-center">
              <div class="status-dot" :class="item.networkStatus ? 'online' : 'offline'" />
            </div>
          </template>
          <span>{{ $t(item.networkStatus ? 'ONLINE' : 'OFFLINE') }}</span>
        </v-tooltip>
      </template>

      <!-- Status -->
      <template v-slot:[`item.status`]="{ item }">
        <span :class="statusStyle(item.status)">{{ item.status }}</span>
      </template>
    </v-data-table>

    <!-- Table Options -->
    <div class="table-options">
      <div>
        <BlinkPopup
          :selectedLabel="selectedLabel"
          :btnDisabledBlinking="btnDisabledBlinking || checkSelected"
          @fireResetLabel="resetSelection"
        />
        <v-btn
          :disabled="tableItems.length === 0 || btnDisabledExport"
          @click="exportList"
          class="btn ml-2"
          text
        >
          {{ $t("Export") }}
        </v-btn>
      </div>
      <div>
        <Pagination
          @paging="paging($event)"
          :pageInfoText="pageInfoText"
          :pageIndex="pageIndex"
          :rowPerPage="rowPerPage"
          :goToPageInput="goToPageInput"
          :totalPages="totalPages"
          @updatePage="updatePage($event)"
        ></Pagination>
      </div>
    </div>
    <a ref="link" :style="{ display: 'none' }" />
  </div>
</template>

<script>
import EventBus from '@/plugins/eventBus'
import { mapGetters } from 'vuex'
import { getRequestConfig, exportFiles } from '@/plugins/exporter'
import commons from '@/plugins/commons'
import codes from '@/plugins/codes'
import Pagenation from '@/mixins/Pagenation'
import Pagination from '@/components/pagination/Pagination.vue'
import BlinkPopup from '../Label/modal/LabelBlinkPopup.vue'

// const dummyItems = [
//   { labelId: '04D86AE3B092', productId: 'L200', coo: 'US', battery: 'GOOD', signalStrength: 'EXCELLENT', type: 'NEWTON_GRAPHIC_1_6_RED_NFC', template: '16NEWTON', templateManual: 'false', networkStatus: 'OFFLINE', status: 'TIMEOUT', lastUpdatedTime: '2026-06-05 08:05:29' },
//   { labelId: '0565E690B792', productId: 'L300', coo: 'CN', battery: 'GOOD', signalStrength: 'GOOD', type: 'NEWTON_GRAPHIC_4_3_RED_NFC', template: '43NEWTONEYE', templateManual: 'false', networkStatus: 'OFFLINE', status: 'TIMEOUT', lastUpdatedTime: '2026-06-05 07:30:39' },
//   { labelId: '0575C18BB299', productId: 'D00011', coo: 'DE', battery: 'GOOD', signalStrength: 'EXCELLENT', type: 'NEWTON_GRAPHIC_2_9_RED_NFC', template: '29newton', templateManual: 'false', networkStatus: 'ONLINE', status: 'SUCCESS', lastUpdatedTime: '2026-07-20 10:34:50' },
//   { labelId: '05D96F33B3DD', productId: 'D00018', coo: 'KR', battery: 'GOOD', signalStrength: 'EXCELLENT', type: 'NEWTON_GRAPHIC_2_6_RED', template: '26_NEWTONEYE', templateManual: 'false', networkStatus: 'ONLINE', status: 'SUCCESS', lastUpdatedTime: '2026-07-20 10:34:50' },
//   { labelId: '07B2FA3CB197', productId: 'E00024', coo: 'JP', battery: 'GOOD', signalStrength: 'EXCELLENT', type: 'NEWTON_GRAPHIC_2_2_RED_NFC', template: '2.2_HD', templateManual: 'false', networkStatus: 'ONLINE', status: 'SUCCESS', lastUpdatedTime: '2026-07-20 12:09:42' },
//   { labelId: '07B2FBD4B190', productId: 'E00025', coo: 'US', battery: 'GOOD', signalStrength: 'EXCELLENT', type: 'NEWTON_GRAPHIC_2_2_RED_NFC', template: '2.2_HD', templateManual: 'false', networkStatus: 'ONLINE', status: 'SUCCESS', lastUpdatedTime: '2026-07-20 10:35:11' },
//   { labelId: '07CAC7FBB498', productId: 'E00026', coo: 'CN', battery: 'GOOD', signalStrength: 'EXCELLENT', type: 'NEWTON_GRAPHIC_2_7_RED_NFC', template: '2.7_HD', templateManual: 'false', networkStatus: 'ONLINE', status: 'SUCCESS', lastUpdatedTime: '2026-07-20 10:55:15' },
//   { labelId: '07D089A1B3D5', productId: 'C00005', coo: 'DE', battery: 'GOOD', signalStrength: 'EXCELLENT', type: 'NEWTON_GRAPHIC_2_6_RED', template: '26_NEWTONEYE', templateManual: 'false', networkStatus: 'ONLINE', status: 'SUCCESS', lastUpdatedTime: '2026-07-20 10:34:50' },
//   { labelId: '07D089A7B3D3', productId: 'J200', coo: 'KR', battery: 'GOOD', signalStrength: 'GOOD', type: 'NEWTON_GRAPHIC_2_6_RED', template: '26_NEWTONEYE', templateManual: 'false', networkStatus: 'ONLINE', status: 'SUCCESS', lastUpdatedTime: '2026-07-20 10:34:56' },
//   { labelId: '07D089A9B3DD', productId: 'E00002', coo: 'JP', battery: 'BAD', signalStrength: 'GOOD', type: 'NEWTON_GRAPHIC_2_6_RED', template: '26_NEWTONEYE', templateManual: 'false', networkStatus: 'ONLINE', status: 'SUCCESS', lastUpdatedTime: '2026-07-20 10:34:56' }
// ]

export default {
  name: 'COOAssignments',
  mixins: [Pagenation],
  components: { Pagination, BlinkPopup },
  data () {
    return {
      user: null,
      btnDisabledExport: null,
      btnDisabledBlinking: null,
      exportDisabled: false,
      menuOpen: false,
      visibleColumns: [],
      selected: [],
      tableItems: [],
      options: {},
      page: 1,
      pageIndex: 1,
      rowPerPage: '10',
      totalPages: 0,
      goToPageInput: 1,
      pageInfoText: null,
      totalRecords: 0,
      hasLoadedAssignments: false,
      isAssignmentsLoading: false,
      requestConfig: {},
      searchBy: {
        labelId: null,
        productId: null,
        coo: null
      }
    }
  },
  computed: {
    isAllSelected () {
      const optional = this.tableHeaders.filter(h => h.value !== 'labelCode')
      return optional.length > 0 && this.visibleColumns.length === optional.length
    },
    filteredHeaders () {
      return this.tableHeaders.filter(
        h => h.value === 'labelCode' || this.visibleColumns.includes(h.text)
      )
    },
    selectedLabel () {
      return this.selected.map(element => element.labelCode)
    },
    checkSelected () {
      return this.selected.length === 0
    },
    tableHeaders () {
      return [
        { text: this.$t('LABEL ID'), value: 'labelCode', sortable: true },
        { text: this.$t('PRODUCT ID'), value: 'productId', sortable: true },
        { text: this.$t('COO'), value: 'coo', sortable: true },
        { text: this.$t('BATTERY'), value: 'battery', sortable: false },
        { text: this.$t('SIGNAL STRENGTH'), value: 'signal', sortable: false },
        { text: this.$t('TYPE'), value: 'type', sortable: false },
        { text: this.$t('NETWORK'), value: 'networkStatus', align: 'center', sortable: false },
        { text: this.$t('STATUS'), value: 'status', sortable: true },
        { text: this.$t('ASSIGN DATE'), value: 'assignDate', sortable: false },
        { text: this.$t('LATEST RESPONSE DATE'), value: 'latestResponseDate', sortable: true }
      ]
    },
    ...mapGetters({ store: 'dataStore/GET_SELECTED_STORE' }),
    selectedStore () {
      return this.$store.getters['dataStore/GET_SELECTED_STORE'].code
    }
  },
  watch: {
    store: {
      handler () {
        this.page = 1
        this.hasLoadedAssignments = false
        this.getCOOAssignments(1)
      }
    },
    options: {
      handler () {
        if (!this.hasLoadedAssignments || this.isAssignmentsLoading) {
          return
        }
        if (this.store.code) {
          this.getCOOAssignments(this.page)
        }
      },
      deep: true
    }
  },
  created () {
    this.user = this.$store.state.auth.user
  },
  mounted () {
    this.toggleSelectAll(true)
    this.togetCustomCloumn()
    this.$store.dispatch('auth/getDisabledBtn', '11500').then(flag => {
      this.btnDisabledExport = flag
    })
    this.$store.dispatch('auth/getDisabledBtn', '11501').then(flag => {
      this.btnDisabledBlinking = flag
    })
    EventBus.$emit('enableSelectedStores', true)
    if (!this.store.code) {
      EventBus.$emit('openSelectedStores')
    } else {
      this.getCOOAssignments(1)
    }
  },
  methods: {
    search (page) {
      this.getCOOAssignments(page)
    },
    clear () {
      this.searchBy = { labelId: null, productId: null, coo: null }
      this.getCOOAssignments(1)
    },
    paging (input) {
      this.goToPageInput = input
      this.getCOOAssignments(input)
    },
    updatePage (event) {
      this.rowPerPage = event.toString()
      this.paging(1)
    },
    saveRequestConfig (config) {
      this.requestConfig = getRequestConfig(config)
    },
    buildParams (page) {
      const { sortBy, sortDesc } = this.options
      const params = {
        company: this.user.company,
        store: this.store.code,
        size: this.rowPerPage
      }
      if (page !== null) params.page = page - 1
      if (commons.isValidStr(this.searchBy.labelId)) params.label = this.searchBy.labelId
      if (commons.isValidStr(this.searchBy.productId)) params.productId = this.searchBy.productId
      if (commons.isValidStr(this.searchBy.coo)) params.coo = this.searchBy.coo
      let sort = null
      if (sortBy && sortBy.length === 1 && sortDesc && sortDesc.length === 1) {
        sort = `${sortBy[0]},${sortDesc[0] ? 'desc' : 'asc'}`
      }
      if (sort !== null) params.sort = sort
      return params
    },
    getCOOAssignments (page) {
      if (commons.isNull(this.store.code) || this.store.code === '') return
      const params = this.buildParams(page)
      const config = { params }
      this.isAssignmentsLoading = true
      this.$utils
        .callAxios(
          codes.requests.getCOOAssignments.method,
          codes.requests.getCOOAssignments.url,
          config
        )
        .then(res => {
          this.saveRequestConfig(res.config)
          if (res.status === 200) {
            this.tableItems = (res.data.cooAssignmentList || []).map(item => ({
              ...item,
              productId: Array.isArray(item.articleIds) && item.articleIds.length > 0
                ? item.articleIds[0]
                : item.productId,
              coo: Array.isArray(item.articleIds) && item.articleIds.length > 1
                ? item.articleIds.slice(1).join(', ')
                : item.coo,
              assignDate: item.assignDate
                ? commons.convertDate(item.assignDate)
                : '-',
              latestResponseDate: item.latestResponseDate
                ? commons.convertDate(item.latestResponseDate)
                : '-'
            }))
            this.selected = []
            this.totalRecords = this.tableItems.length
            this.pageInfoText = this.getPageInfoText(res.headers)
            if (commons.isValidStr(res.headers['x-totalpages'])) {
              this.totalPages = res.headers['x-totalpages'] * 1
            }
            this.pageIndex = res.headers['x-number'] * 1 + 1
          } else {
            this.tableItems = []
            this.totalRecords = 0
            this.totalPages = 0
          }
        })
        .catch(error => {
          this.tableItems = []
          this.totalRecords = 0
          this.totalPages = 0
          const headers = {}
          headers['x-totalelements'] = 0
          this.pageInfoText = this.getPageInfoText(headers)
          console.debug(error)
        })
        .finally(() => {
          this.isAssignmentsLoading = false
          this.hasLoadedAssignments = true
        })
    },
    getPageInfoText (headers) {
      if (headers['x-totalelements'] === '0' || headers['x-totalelements'] === 0) {
        return '0 to 0, 0 ' + this.$t('in total')
      }
      return (
        `${headers['x-number'] * 1 * headers['x-size'] + 1} ` +
        `${this.$t('to')} ${headers['x-number'] * 1 * headers['x-size'] + headers['x-total-count'] * 1}, ` +
        `${headers['x-totalelements'] * 1} ` +
        this.$t('in total')
      )
    },
    toggleSelectAll (value) {
      if (value) {
        this.visibleColumns = this.tableHeaders
          .filter(h => h.value !== 'labelCode')
          .map(h => h.text)
      } else {
        this.visibleColumns = []
      }
    },
    statusStyle (status) {
      if (status === 'SUCCESS') return 'txt-stable'
      if (status === 'PROCESSING') return 'txt-caution'
      if (status === 'TIMEOUT') return 'txt-danger'
      return ''
    },
    togetCustomCloumn () {
      const params = { userId: this.$store.state.auth.user.account, type: 'COO_ASSIGNMENTS' }
      const config = { params: params }
      this.$utils
        .callAxios(
          codes.requests.getCustomColoumnlist.method,
          codes.requests.getCustomColoumnlist.url,
          config
        )
        .then(res => {
          if (res.status === 200) {
            const data = res.data
            const apiColumns = data['11500'] || []
            if (apiColumns.length > 0) {
              this.visibleColumns = apiColumns.filter(text =>
                this.tableHeaders.some(h => h.value !== 'labelCode' && h.text === text)
              )
            } else {
              this.visibleColumns = []
            }
          } else {
            this.visibleColumns = this.tableHeaders
              .filter(h => h.value !== 'labelCode')
              .map(h => h.text)
          }
        })
        .catch(error => {
          console.log(error)
          this.visibleColumns = this.tableHeaders
            .filter(h => h.value !== 'labelCode')
            .map(h => h.text)
        })
    },
    SaveVisibleColumns () {
      const body = {
        11500: this.visibleColumns
      }
      const params = { userId: this.$store.state.auth.user.account, type: 'COO_ASSIGNMENTS' }
      this.$utils
        .callAxiosWithBody(
          codes.requests.updateCustomColoumnlist.method,
          codes.requests.updateCustomColoumnlist.url,
          body,
          { params: params }
        )
        .then(res => {
          if (res.status === 200) {
            EventBus.$emit('messageAlert', this.$t(res.data.responseMessage))
            this.togetCustomCloumn()
          }
        })
        .catch(error => {
          console.log(error)
        })
    },
    exportList () {
      exportFiles(this.requestConfig, this.$refs.link, 'COOassignment.xlsx')
    },
    resetSelection () {
      this.selected = []
      this.getCOOAssignments(this.page)
    }
  }
}
</script>

<style scoped>
.imgageproperty {
  margin-right: 14px;
  width: 28px;
}

::v-deep ::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::v-deep ::-webkit-scrollbar-track {
  background: var(--lightestgrey);
}

::v-deep ::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 5px;
}

::v-deep ::-webkit-scrollbar-thumb:hover {
  background: #adadad;
}

::v-deep .v-data-table thead th {
  white-space: nowrap;
}

.columnMenu  { max-width: 260px; }
.column-card { width: 100%; max-width: 260px; }
.column-header { font-weight: 600; font-size: 14px; padding: 12px 16px; }
.column-list   { max-height: 260px; overflow-y: auto; padding: 0 8px; }
.column-text   { font-size: 13px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.column-footer { position: sticky; bottom: 0; background: #fff; padding: 8px 12px; }
.manage-btn    { max-width: 200px; white-space: nowrap; }

.coo-search-row { gap: 8px; }
.coo-field { flex: 1 1 150px; min-width: 130px; max-width: 260px; }
.coo-btn   { flex: 0 0 110px; min-width: 110px !important; padding-bottom: 4px !important; }

@media (max-width: 1275px) {
  .coo-search-row { flex-wrap: wrap !important; }
}

@media (max-width: 960px) {
  .coo-field { flex: 1 1 calc(50% - 4px); max-width: none; }
  .coo-btn   { flex: 1 1 calc(50% - 4px); max-width: none; }
  .manage-btn { max-width: 100%; }
}

@media (max-width: 600px) {
  .coo-field { flex: 1 1 100%; max-width: none; }
  .coo-btn   { flex: 1 1 100%; max-width: none; }
  .manage-btn { width: 100%; max-width: 100%; }
}

.rotate {
  transform: rotate(180deg);
  transition: transform 0.2s ease;
}

::v-deep .v-input--selection-controls {
  margin-top: 0px !important;
  padding-top: 0px !important;
}

.status-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
}

.status-dot.online  { background-color: #9bcde3; }
.status-dot.offline { background-color: #e27070; }
</style>

<style>
.tooltip-content {
  background: #001e38;
  color: #fff;
  padding: 5px 10px;
}
</style>
